import axiosInstance from '../../services/axiosConfig';

// Auth related services
export const authService = {
  signup: async (companyData) => {
    try {
      const response = await axiosInstance.post('/company/signup', companyData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error?.message ||
        'Signup failed';
      throw new Error(errorMessage);
    }
  },

  login: async (credentials) => {
    try {
      const response = await axiosInstance.post('/company/login', credentials);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Login failed';
      throw error.response?.data || { message: errorMessage };
    }
  },

  logout: async () => {
    try {
      const response = await axiosInstance.post('/company/logout');
      localStorage.removeItem('token');
      localStorage.removeItem('company');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Logout failed';
      throw new Error(errorMessage);
    }
  },

  verifyEmail: async (code) => {
    try {
      const response = await axiosInstance.post('/company/verify-email', { code });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Email verification failed';
      throw new Error(errorMessage);
    }
  },

  resendVerification: async (email) => {
    try {
      const response = await axiosInstance.post('/company/resend-verification', { email });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to resend verification email';
      throw new Error(errorMessage);
    }
  },

  forgotPassword: async (email) => {
    try {
      const response = await axiosInstance.post('/company/forgot-password', { email });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Password reset request failed';
      throw new Error(errorMessage);
    }
  },

  resetPassword: async (token, passwords) => {
    try {
      const response = await axiosInstance.post(`/company/reset-password/${token}`, passwords);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Password reset failed';
      throw new Error(errorMessage);
    }
  },

  getProfile: async () => {
    try {
      const response = await axiosInstance.get('/company/profile');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch company profile';
      throw new Error(errorMessage);
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await axiosInstance.put('/company/profile', profileData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to update company profile';
      throw new Error(errorMessage);
    }
  },

  uploadProfileImage: async (imageData) => {
    try {
      const response = await axiosInstance.post('/company/profile/image', { image: imageData });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to upload profile image';
      throw new Error(errorMessage);
    }
  }
};

// Brand related services
export const brandService = {
  createBrand: async (brandData) => {
    try {
      const response = await axiosInstance.post('/company/brands', brandData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to create brand';
      throw new Error(errorMessage);
    }
  },

  getBrands: async () => {
    try {
      const response = await axiosInstance.get('/company/brands');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch brands';
      throw new Error(errorMessage);
    }
  },

  getBrandById: async (brandId) => {
    try {
      const response = await axiosInstance.get(`/company/brands/${brandId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch brand details';
      throw new Error(errorMessage);
    }
  },

  updateBrand: async (brandId, brandData) => {
    try {
      const response = await axiosInstance.put(`/company/brands/${brandId}`, brandData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to update brand';
      throw new Error(errorMessage);
    }
  },

  updateBrandStatus: async (brandId, status) => {
    try {
      const response = await axiosInstance.patch(`/company/brands/${brandId}`, { status });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to update brand status';
      throw new Error(errorMessage);
    }
  }
};

// Brand Manager related services
export const brandManagerService = {
  createBrandManager: async (managerData) => {
    try {
      const response = await axiosInstance.post('/company/brand-managers', managerData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to create brand manager';
      throw new Error(errorMessage);
    }
  },

  getBrandManagers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/company/brand-managers${queryParams ? `?${queryParams}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch brand managers';
      throw new Error(errorMessage);
    }
  },

  getBrandManagerById: async (managerId) => {
    try {
      const response = await axiosInstance.get(`/company/brand-managers/${managerId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch brand manager details';
      throw new Error(errorMessage);
    }
  },

  updateBrandManager: async (managerId, managerData) => {
    try {
      const response = await axiosInstance.put(`/company/brand-managers/${managerId}`, managerData);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to update brand manager';
      throw new Error(errorMessage);
    }
  },

  updateBrandManagerStatus: async (managerId, status) => {
    try {
      const response = await axiosInstance.patch(`/company/brand-managers/${managerId}/status`, { status });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to update brand manager status';
      throw new Error(errorMessage);
    }
  },

  deleteBrandManager: async (managerId) => {
    try {
      const response = await axiosInstance.delete(`/company/brand-managers/${managerId}`);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to delete brand manager';
      throw new Error(errorMessage);
    }
  },

  getBrandManagerStats: async () => {
    try {
      const response = await axiosInstance.get('/company/brand-managers/stats');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to fetch brand manager statistics';
      throw new Error(errorMessage);
    }
  }
};

// Campaign related services
export const campaignService = {
  createCampaign: async (campaignData) => {
    try {
      const response = await axiosInstance.post('/company/campaigns', campaignData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create campaign' };
    }
  },

  getCampaigns: async () => {
    try {
      const response = await axiosInstance.get('/company/campaigns');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch campaigns' };
    }
  },

  getCampaignLimits: async () => {
    try {
      const response = await axiosInstance.get('/company/campaigns/limits');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch campaign limits' };
    }
  },

  getCampaignById: async (id) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch campaign' };
    }
  },

  updateCampaign: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/company/campaigns/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update campaign' };
    }
  },

  updateCampaignStatus: async (id, payload) => {
    try {
      const response = await axiosInstance.patch(`/company/campaigns/${id}`, payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update campaign status' };
    }
  },

  getCampaignAffiliates: async (campaignId) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/affiliates`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch campaign affiliates' };
    }
  },

  getAffiliatePaymentDetails: async (campaignId, affiliateId) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/payment`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch payment details' };
    }
  },

  updateAffiliatePaymentDetails: async (campaignId, affiliateId, paymentData) => {
    try {
      const response = await axiosInstance.put(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/payment`, paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update payment details' };
    }
  },

  calculateAffiliatePayment: async (campaignId, affiliateId) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/calculate-payment`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to calculate payment' };
    }
  },

  getAllPaymentRecords: async () => {
    try {
      const response = await axiosInstance.get('/company/campaigns/payment-records/all');
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch payment records' };
    }
  },

  getAffiliateAgreement: async (campaignId, affiliateId) => {
    try {
      const response = await axiosInstance.get(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/agreement`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch affiliate agreement' };
    }
  },

  updateAffiliateAgreement: async (campaignId, affiliateId, agreementData) => {
    try {
      const response = await axiosInstance.put(`/company/campaigns/${campaignId}/affiliate/${affiliateId}/agreement`, agreementData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update affiliate agreement' };
    }
  }
};

// Affiliate related services
export const affiliateService = {
  createAffiliate: async (affiliateData) => {
    try {
      const response = await axiosInstance.post('/company/affiliates', affiliateData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to create affiliate' };
    }
  },

  getAffiliates: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/company/affiliates', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch affiliates' };
    }
  },
  getAffiliateById: async (id) => {
    try {
      const response = await axiosInstance.get(`/company/affiliates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch affiliate' };
    }
  },

  getInvitationsByEmail: async (email) => {
    try {
      const response = await axiosInstance.get(`/company/affiliate-invitations/by-email/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch affiliate' };
    }
  },

  updateAffiliate: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/company/affiliates/${id}`, {
        profile: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          affiliateType: data.affiliateType,
          companyName: data.companyName,
          instagramId: data.instagramId
        },
        payment: {
          method: data.paymentInfo.method,
          bankName: data.paymentInfo.bankName,
          accountNumber: data.paymentInfo.accountNumber,
          ifscCode: data.paymentInfo.ifscCode,
          upiId: data.paymentInfo.upiId
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update affiliate' };
    }
  },

  updateAffiliateStatus: async (id, status) => {
    try {
      const response = await axiosInstance.patch(`/company/affiliates/${id}`, { status });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to update affiliate status' };
    }
  },

  deleteAffiliate: async (id) => {
    try {
      const response = await axiosInstance.delete(`/company/affiliates/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete affiliate' };
    }
  },

  // deleteCampaignInvitation: async (affiliateId, campaignId) => {
  //   try {
  //     const response = await axiosInstance.delete(`/company/affiliates/${affiliateId}/campaigns/${campaignId}`);
  //     return response.data;
  //   } catch (error) {
  //     throw error.response?.data || { message: 'Failed to delete campaign invitation' };
  //   }
  // },

  deleteCampaignInvitationByEmail: async (email, campaignId) => {
    try {
      const encodedEmail = encodeURIComponent(email);
      const response = await axiosInstance.delete(`/company/affiliates/email/${encodedEmail}/campaigns/${campaignId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to delete campaign invitation' };
    }
  }
};

// Campaign-Affiliate association services
export const campaignAffiliateService = {
  associateAffiliateWithCampaign: async (campaignId, affiliateId) => {
    try {
      const response = await axiosInstance.post(`/company/campaigns/${campaignId}/affiliates`, { affiliateId });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to associate affiliate with campaign' };
    }
  },

  removeAffiliateFromCampaign: async (campaignId, affiliateId) => {
    try {
      const response = await axiosInstance.delete(`/company/campaigns/${campaignId}/affiliates/${affiliateId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to remove affiliate from campaign' };
    }
  },

  getAffiliateCampaigns: async (affiliateId) => {
    try {
      const response = await axiosInstance.get(`/company/affiliates/${affiliateId}/campaigns`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Failed to fetch affiliate campaigns' };
    }
  },
};

// Social accounts (e.g., Instagram) services
export const socialAccountsService = {
  /**
   * Start Instagram connect flow for a company.
   * Returns { success, authUrl } and then caller should redirect the browser.
   */
  getInstagramConnectUrl: async (redirectTo) => {
    try {
      const params = redirectTo
        ? `?redirectTo=${encodeURIComponent(redirectTo)}`
        : '';
      const response = await axiosInstance.get(
        `/company/instagram${params}`
      );
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to initiate Instagram connect';
      throw new Error(errorMessage);
    }
  },

  getInstagramStatus: async () => {
    try {
      const response = await axiosInstance.get('/company/instagram/status');
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to fetch Instagram status';
      throw new Error(errorMessage);
    }
  },
};

// For backward compatibility
export const {
  signup,
  login,
  logout,
  verifyEmail,
  resendVerification: resendVerificationEmail,
  forgotPassword,
  resetPassword,
  getProfile: getCompanyProfile,
  updateProfile: updateCompanyProfile
} = authService;

export const {
  createBrand,
  getBrands: getBrandsByCompany,
  getBrandById,
  updateBrand,
  updateBrandStatus
} = brandService;

export const {
  createCampaign,
  getCampaigns,
  getCampaignById,
  updateCampaign,
  updateCampaignStatus,
  getAffiliatePaymentDetails,
  updateAffiliatePaymentDetails,
  calculateAffiliatePayment
} = campaignService;

export const {
  createAffiliate,
  getAffiliates,
  getAffiliateById,
  getInvitationsByEmail,
  updateAffiliate,
  updateAffiliateStatus,
  deleteAffiliate,
  // deleteCampaignInvitation,
  deleteCampaignInvitationByEmail
} = affiliateService;

export const {
  associateAffiliateWithCampaign,
  removeAffiliateFromCampaign,
  getAffiliateCampaigns,
  getCampaignAffiliates
} = campaignAffiliateService;

export const uploadProfileImage = async (imageData) => {
  try {
    const response = await axiosInstance.post('/company/profile/image', { image: imageData });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: 'Failed to upload profile image' };
  }
};


